import { useRouteQuery } from '@/core/browser/route-query.composable';
import { QueryKeys } from '@/shared/config/route-query.config';
import { computed, reactive, watch } from 'vue';
import {
    BaseProductItem,
    BasketCheckoutModel,
    BasketCheckoutModelSummary,
    BasketDeliveryAddress,
    BasketInvoiceAddress,
    DeliveryTimesForCustomerDelivery,
    DeliveryOptionModel,
    EarliestCustomerDeliveryDates,
    PickUpPointsForDelivery,
    PickUpTimesForCustomerDelivery,
    SetDeliveryTimeRequest,
    B2CClientService,
    AddInvoiceAddressToBasketRequest,
    DeliveryDateAndFreightPriceForSplitItemsInCustomerDelivery, CheckoutDeliverableType,
} from '@/b2c/shared/api/server-apis';
import useBasket from '@/b2c/basket/useBasket';
import utilitiesService from '@/shared/utilities.service';
// import { hardRedirect } from '@/core/navigation/redirect.service';

export interface ICheckoutState {
    step: number;
    basket: BasketCheckoutModel;
    deliveryInfo: DeliverableItemsDeliveryInfo;
    paymentAddressValidationHandler: (() => Promise<boolean>) | null;
    isPaymentReady: boolean;
    itemIdsOutOfStock: number[];
}

export interface DeliverableItemsDeliveryInfo {
    [id: number]: EarliestCustomerDeliveryDates,
}

const state: ICheckoutState = reactive({
    step: 0,
    basket: {} as BasketCheckoutModel,
    deliveryInfo: {} as DeliverableItemsDeliveryInfo,
    selectedPaymentOption: null,
    paymentAddressValidationHandler: null,
    isPaymentReady: false,
    itemIdsOutOfStock: [] as number[],
    showChooseSalesPersonDialog: false,
});

export function useCheckout() {
    const { basketId } = useBasket();
    const stepQuery = useRouteQuery<number>(QueryKeys.CheckoutStep);

    const step = computed<number>(() => {
        return state.step;
    });

    const isAddressStep = computed<boolean>(() => {
        return state.step === 0;
    });

    const isDeliverableStep = computed<boolean>(() => {
        return state.step !== 0;
    });

    const deliverableCount = computed<number>(() => {
        return state.basket.deliverables?.length ?? 0;
    });

    const isLastStep = computed<boolean>(() => {
        return state.basket.deliverables?.length === state.step ?? false;
    });

    const items = computed<BaseProductItem[]>(() => {
        return state.basket.items ?? [] as BaseProductItem[];
    });

    const summary = computed<BasketCheckoutModelSummary>(() => {
        return state.basket.summary ?? {} as BasketCheckoutModelSummary;
    });

    const deliveryInfo = computed<DeliverableItemsDeliveryInfo>(() => {
        return state.deliveryInfo ?? {} as DeliverableItemsDeliveryInfo;
    });

    const invoiceAddress = computed<BasketInvoiceAddress | undefined>(() => {
        return state.basket.invoiceAddress;
    });

    const basket = computed<BasketCheckoutModel>(() => {
        return state.basket ?? {} as BasketCheckoutModel;
    });

    const isPaymentReady = computed<boolean>(() => {
        return state.isPaymentReady;
    });

    const itemIdsOutOfStock = computed<number[]>(() => {
        return state.itemIdsOutOfStock;
    });

    const isClickAndCollectOnly = computed<boolean>(() => {
        return state.basket.deliverables.length === 1 && state.basket.deliverables[0].type === CheckoutDeliverableType.ClickAndCollect;
    });

    const init = async(): Promise<void> => {
        const response = await B2CClientService.getBasketForCheckOut(basketId.value).then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const setDeliveryAddress = async(deliverableId: number, request: BasketDeliveryAddress): Promise<void> => {
        const response = await B2CClientService
            .setDeliveryAddress(basketId.value, deliverableId, request)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const getPickUpErrorMessageId = (id: number) => `pickUpErrorMessageId-${id}`;

    const setDeliveryTime = async(deliverableId: number, request: SetDeliveryTimeRequest, messageId?: string): Promise<void> => {
        const response = await B2CClientService
            .setDeliveryTime(basketId.value, deliverableId, request, messageId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const addInvoiceAddress = async(request: AddInvoiceAddressToBasketRequest): Promise<void> => {
        const response = await B2CClientService
            .addInvoiceAddress(basketId.value, request)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const setDeliveryInformationForItems = async(deliverableId: number): Promise<void> => {
        const response = await B2CClientService
            .getEarliestDeliveryDateForCustomerDelivery(basketId.value, deliverableId)
            .then(utilitiesService.unwrapModel);
        if (response) {
            state.deliveryInfo[deliverableId] = response;
        };
    };

    const clearDeliveryInformationForItems = (deliverableId: number): void => {
        if (state.deliveryInfo[deliverableId]) {
            delete state.deliveryInfo[deliverableId];
        }
    };

    const getDeliveryTimesForCustomerDelivery = async(deliverableId: number): Promise<DeliveryTimesForCustomerDelivery> => {
        const response = await B2CClientService
            .getDeliveryTimesForCustomerDelivery(basketId.value, deliverableId)
            .then(utilitiesService.unwrapModel);
        return response ?? {} as DeliveryTimesForCustomerDelivery;
    };

    const addDeliveryOption = async(deliverableId: number, itemId: string): Promise<void> => {
        const response = await B2CClientService
            .addDeliveryOption(basketId.value, deliverableId, itemId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const removeDeliveryOption = async(deliverableId: number, itemId: string): Promise<void> => {
        const response = await B2CClientService
            .removeDeliveryOption(basketId.value, deliverableId, itemId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const getDeliveryOptions = async(countryCode: string): Promise<(DeliveryOptionModel|undefined)[]> => {
        const response = await B2CClientService
            .getDeliveryOptions(countryCode, basketId.value)
            .then(utilitiesService.unwrapModel);
        return response ?? [] as DeliveryOptionModel[];
    };

    const getPickUpPointsForDelivery = async(deliverableId: number): Promise<PickUpPointsForDelivery> => {
        const response = await B2CClientService
            .getPickUpPointsForDelivery(basketId.value, deliverableId)
            .then(utilitiesService.unwrapModel);
        return response ?? {} as PickUpPointsForDelivery;
    };

    const getPickUpTimesForCustomerDelivery = async(deliverableId: number, hubId: string): Promise<PickUpTimesForCustomerDelivery> => {
        const response = await B2CClientService
            .getPickUpTimesForCustomerDelivery(basketId.value, deliverableId, hubId)
            .then(utilitiesService.unwrapModel);
        return response ?? {} as PickUpTimesForCustomerDelivery;
    };

    const changeClickAndCollectStoreFromCheckOut = async(storeId: string): Promise<void> => {
        const response = await B2CClientService
            .changeClickAndCollectStoreFromCheckOut(basketId.value, storeId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const splitDelivery = async(deliveryId: number, itemIds: number[]): Promise<void> => {
        const response = await B2CClientService
            .splitDelivery(basketId.value, deliveryId, itemIds)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const removeSplitDelivery = async(deliveryId: number): Promise<void> => {
        const response = await B2CClientService
            .removeSplitDelivery(basketId.value, deliveryId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
    };

    const removeSplitDeliveries = async(deliveryId: number): Promise<BasketCheckoutModel> => {
        const response = await B2CClientService
            .removeSplitDeliveries(basketId.value, deliveryId)
            .then(utilitiesService.unwrapModel);
        state.basket = response ?? {} as BasketCheckoutModel;
        return state.basket;
    };

    const getDeliveryDateAndFreightPriceForSplitItems = async(deliveryId: number, itemIds: number[]): Promise<DeliveryDateAndFreightPriceForSplitItemsInCustomerDelivery> => {
        const response = await B2CClientService
            .getDeliveryDateAndFreightPriceForSplitItemsInCustomerDelivery(basketId.value, deliveryId, itemIds)
            .then(utilitiesService.unwrapModel);
        return response ?? {} as DeliveryDateAndFreightPriceForSplitItemsInCustomerDelivery;
    };

    const setPaymentAddressValidationHandler = (handler: () => Promise<boolean>): void => {
        state.paymentAddressValidationHandler = handler;
    };

    // TODO: Initiate payment correctly but awaits Altapay implementation
    const initiatePayment = async(): Promise<void> => {
        if (state.paymentAddressValidationHandler) {
            await state.paymentAddressValidationHandler();
        }
    };

    // const setItemIdsOutOfStock = (items?: number[]): void => {
    //     state.itemIdsOutOfStock = items?.length ? items : [];
    // };

    const clearItemIdsOutOfStock = (): void => {
        state.itemIdsOutOfStock.length = 0;
    };

    // const gotoOnlinePayment = async(request: CheckOutOnlinePaymentRequest): Promise<void> => {
    //     const response = await B2CClientService
    //         .checkOutOnlinePayment(basketId.value, request)
    //         .then(utilitiesService.unwrapModel);
    //     if (response?.isCheckoutAble && response?.redirectUrl) {
    //         hardRedirect(response.redirectUrl);
    //     }
    //     setItemIdsOutOfStock(response?.itemsOutOfStock);
    // };

    const setIsPaymentReady = (ready: boolean): void => {
        state.isPaymentReady = ready;
    };

    const updateStep = (step: number): void => {
        stepQuery.value = step;
    };

    const gotoNextStep = (): void => {
        if (!isLastStep.value) {
            stepQuery.value = state.step + 1;
        }
    };

    watch(stepQuery, step => {
        state.step = step;
    }, { immediate: true });

    return {
        items,
        summary,
        invoiceAddress,
        deliveryInfo,
        deliverableCount,
        basket,
        step,
        isAddressStep,
        isDeliverableStep,
        isLastStep,
        isPaymentReady,
        itemIdsOutOfStock,
        isClickAndCollectOnly,
        init,
        setDeliveryAddress,
        addInvoiceAddress,
        setDeliveryInformationForItems,
        clearDeliveryInformationForItems,
        getPickUpErrorMessageId,
        setDeliveryTime,
        getDeliveryTimesForCustomerDelivery,
        getDeliveryOptions,
        addDeliveryOption,
        removeDeliveryOption,
        getPickUpPointsForDelivery,
        getPickUpTimesForCustomerDelivery,
        changeClickAndCollectStoreFromCheckOut,
        initiatePayment,
        // gotoOnlinePayment,
        setPaymentAddressValidationHandler,
        setIsPaymentReady,
        clearItemIdsOutOfStock,
        updateStep,
        gotoNextStep,
        splitDelivery,
        removeSplitDelivery,
        removeSplitDeliveries,
        getDeliveryDateAndFreightPriceForSplitItems,
    };
}
